/**
 * Consent Manager - Wraps @dcbl/cookie-consent library
 * Handles initialization, consent state, and event dispatching
 */

import {
	createCmpClient,
	getConsentCookie,
	setConsentCookie,
	hasConsentFor as cmpHasConsentFor,
	setGoogleConsentDefaults,
	updateGoogleConsent,
	processConsent,
	pushConsentEvents,
	dispatchConsentEvent,
	unblockScripts,
	unblockIframes,
	type CmpClient,
	type ConsentCookieData,
	type ConsentCategories,
	type ConsentServices,
	type Cookie,
} from "@dcbl/cookie-consent";

import type { PluginConfig, ServiceInfo, ServicesByCategory } from "./config";
import { Modal } from "./modal";
import { ReopenButton } from "./reopen-button";

export type ConsentEventType =
	| "init"
	| "consent:first-action"
	| "consent:accepted"
	| "consent:changed"
	| "modal:show"
	| "modal:hide";

export type ConsentEventCallback = (data?: ConsentCategories) => void;

export class ConsentManager {
	private config: PluginConfig;
	private modal: Modal;
	private reopenButton: ReopenButton;
	private cmpClient: CmpClient | null = null;
	private cookieData: Cookie[] | null = null;
	private eventListeners: Map<ConsentEventType, Set<ConsentEventCallback>> =
		new Map();
	private initialized = false;
	private currentConsent: ConsentCategories | null = null;
	private currentServices: ServicesByCategory | null = null;

	constructor(config: PluginConfig) {
		this.config = config;
		this.modal = new Modal(this, config);
		this.reopenButton = new ReopenButton(this, config);

		// Set Google Consent Mode defaults before anything else
		this.setConsentDefaults();

		// Create CMP client if configured
		if (config.cmp?.apiUrl && config.cmp?.projectCode) {
			this.cmpClient = createCmpClient({
				apiUrl: config.cmp.apiUrl,
				projectCode: config.cmp.projectCode,
				environment: config.cmp.environment,
			});

			// Fetch cookie data from CMP (async, non-blocking)
			this.cmpClient
				.fetchCookies({ locale: config.locale })
				.then((response) => {
					if (response) {
						this.cookieData = response.cookies;
					}
				})
				.catch((err) => {
					console.warn(
						"[DCBL Cookie Consent] Failed to fetch cookies from CMP:",
						err,
					);
				});
		}

		// Initialize after optional delay
		if (config.delay > 0) {
			setTimeout(() => this.initialize(), config.delay);
		} else {
			this.initialize();
		}
	}

	private setConsentDefaults(): void {
		// Build default consent states based on storage configuration
		const defaults: ConsentCategories = {};

		for (const [key, storage] of Object.entries(this.config.storages)) {
			if (storage?.enabled) {
				defaults[key] = storage.default ? true : false;
			}
		}

		// Set Google Consent Mode defaults
		setGoogleConsentDefaults(defaults);
	}

	private initialize(): void {
		// Check for existing consent
		const existingConsent = getConsentCookie(this.config.cookie.name);

		if (existingConsent && existingConsent.categories) {
			// Check revision
			if (existingConsent.revision !== this.config.revision) {
				// Revision changed, need to re-show modal
				if (this.config.autoShow) {
					this.showModal();
				}
			} else {
				// Apply existing consent
				this.currentConsent = existingConsent.categories;
				this.currentServices = existingConsent.services ?? null;
				updateGoogleConsent(existingConsent.categories);
				unblockScripts(existingConsent.categories, existingConsent.services);
				unblockIframes(existingConsent.categories, existingConsent.services);
				this.emit("consent:accepted", existingConsent.categories);

				// Show reopen button since consent exists
				this.reopenButton.create();
				this.reopenButton.show();
			}
		} else if (this.config.autoShow) {
			// No existing consent, show modal
			this.showModal();
		}

		this.initialized = true;
		this.emit("init");

		// Set up global event interface
		this.setupGlobalEventInterface();

		// Set up embed placeholder click handlers
		this.setupPlaceholderHandlers();
	}

	/**
	 * Set up click handlers for embed placeholder buttons.
	 * Uses event delegation to handle dynamically inserted placeholders.
	 */
	private setupPlaceholderHandlers(): void {
		document.addEventListener("click", (e) => {
			const target = e.target as HTMLElement;

			// Handle "Allow [Provider]" button in placeholders
			const acceptBtn = target.closest(
				".dcbl-cc-embed-placeholder__accept-provider",
			);
			if (acceptBtn) {
				e.preventDefault();
				const placeholder = acceptBtn.closest(".dcbl-cc-embed-placeholder");
				if (placeholder) {
					const category = placeholder.getAttribute("data-category");
					const provider = placeholder.getAttribute("data-provider");
					if (category && provider) {
						this.acceptProvider(category, provider);
					}
				}
				return;
			}

			// Handle "Manage cookie preferences" button in placeholders
			const settingsBtn = target.closest(
				".dcbl-cc-embed-placeholder__settings-link",
			);
			if (settingsBtn) {
				e.preventDefault();
				this.showSettings();
			}
		});
	}

	private setupGlobalEventInterface(): void {
		const pendingEvents = (
			window as unknown as {
				dcblCookieConsentEvents?: Array<
					[ConsentEventType, ConsentEventCallback]
				>;
			}
		).dcblCookieConsentEvents;

		if (Array.isArray(pendingEvents)) {
			for (const [event, callback] of pendingEvents) {
				this.on(event, callback);
			}
		}

		(
			window as unknown as {
				dcblCookieConsentEvents: {
					push: (entry: [ConsentEventType, ConsentEventCallback]) => void;
				};
			}
		).dcblCookieConsentEvents = {
			push: (entry: [ConsentEventType, ConsentEventCallback]) => {
				const [event, callback] = entry;
				this.on(event, callback);
			},
		};
	}

	/**
	 * Public API
	 */

	public showModal(): void {
		this.reopenButton.hide();
		this.modal.show();
		this.emit("modal:show");
	}

	public hideModal(): void {
		this.modal.hide();
		this.emit("modal:hide");

		// Show reopen button if consent has been given
		if (this.currentConsent) {
			this.reopenButton.create();
			this.reopenButton.show();
			this.reopenButton.focus();
		}
	}

	public showSettings(): void {
		this.reopenButton.hide();
		this.modal.showSettings();
	}

	public acceptAll(): void {
		const { categories, services } = this.buildConsentState(true);
		this.applyConsent(categories, services, !this.currentConsent);
	}

	public rejectAll(): void {
		const { categories, services } = this.buildConsentState(false);
		this.applyConsent(categories, services, !this.currentConsent);
	}

	public savePreferences(
		categoryPreferences: Record<string, boolean>,
		servicePreferences?: ServicesByCategory,
	): void {
		const { categories, services } = this.buildConsentState(
			false,
			categoryPreferences,
			servicePreferences,
		);
		this.applyConsent(categories, services, !this.currentConsent);
	}

	public hasConsentFor(storage: string): boolean {
		return cmpHasConsentFor(storage, this.config.cookie.name);
	}

	public getConfig(): PluginConfig {
		return { ...this.config };
	}

	public getTranslation(key: string): string {
		const locale = this.config.locale;
		const translations =
			this.config.translations[locale] || this.config.translations["en"] || {};
		return (translations as Record<string, string>)[key] || key;
	}

	public getCurrentConsent(): ConsentCategories | null {
		return this.currentConsent ? { ...this.currentConsent } : null;
	}

	/** Get the CMP client instance (null if CMP is not configured) */
	public getCmpClient(): CmpClient | null {
		return this.cmpClient;
	}

	/** Get cookie data fetched from CMP, grouped by category code */
	public getCookiesByCategory(): Map<string, Cookie[]> {
		const grouped = new Map<string, Cookie[]>();
		if (!this.cookieData) return grouped;

		for (const cookie of this.cookieData) {
			const code = cookie.category.code;
			if (!grouped.has(code)) {
				grouped.set(code, []);
			}
			grouped.get(code)!.push(cookie);
		}

		return grouped;
	}

	/** Get providers grouped by category code, deduplicated */
	public getProvidersByCategory(): Map<string, ServiceInfo[]> {
		const grouped = new Map<string, ServiceInfo[]>();
		if (!this.cookieData) return grouped;

		for (const cookie of this.cookieData) {
			const catCode = cookie.category.code;
			if (!grouped.has(catCode)) {
				grouped.set(catCode, []);
			}
			const providers = grouped.get(catCode)!;
			if (!providers.some((p) => p.code === cookie.cookieProvider.code)) {
				providers.push({
					code: cookie.cookieProvider.code,
					name: cookie.cookieProvider.name,
					type: cookie.cookieProvider.type,
					link: cookie.cookieProvider.link,
				});
			}
		}

		return grouped;
	}

	/** Get current per-service consent state */
	public getCurrentServices(): ServicesByCategory | null {
		return this.currentServices ? { ...this.currentServices } : null;
	}

	/** Check if a specific service is consented within a category */
	public hasServiceConsent(categoryCode: string, serviceCode: string): boolean {
		if (!this.currentServices) {
			// Fallback to category-level
			return this.currentConsent?.[categoryCode] === true;
		}
		return this.currentServices[categoryCode]?.includes(serviceCode) ?? false;
	}

	/**
	 * Accept a specific provider within a category.
	 * This grants consent for the category but only for the specified provider(s).
	 * Other providers in the same category remain blocked until explicitly accepted.
	 *
	 * @param categoryCode - The consent category (e.g., 'ad_storage')
	 * @param providerCode - The provider to accept (e.g., 'youtube')
	 *
	 * @example
	 * ```typescript
	 * // Accept only YouTube within ad_storage
	 * DCBLCookieConsent.acceptProvider('ad_storage', 'youtube');
	 *
	 * // Later, also accept Vimeo
	 * DCBLCookieConsent.acceptProvider('ad_storage', 'vimeo');
	 * ```
	 */
	public acceptProvider(categoryCode: string, providerCode: string): void {
		const isFirstAction = !this.currentConsent;

		// Start from current state or defaults
		const categories: ConsentCategories = { ...this.currentConsent };
		const services: ServicesByCategory = { ...this.currentServices };

		// Ensure category is consented
		categories[categoryCode] = true;

		// Add provider to the services list for this category
		if (!services[categoryCode]) {
			services[categoryCode] = [];
		}
		if (!services[categoryCode].includes(providerCode)) {
			services[categoryCode] = [...services[categoryCode], providerCode];
		}

		// Also ensure all necessary/readonly categories are consented
		for (const [key, storage] of Object.entries(this.config.storages)) {
			if (storage?.enabled && storage.readonly) {
				categories[key] = storage.default;
			}
		}

		this.applyConsent(categories, services, isFirstAction);
	}

	/**
	 * Get the category code for a given provider.
	 * Looks up from the embed_provider_categories mapping or CMP cookie data.
	 */
	public getCategoryForProvider(providerCode: string): string | null {
		// First check CMP cookie data
		if (this.cookieData) {
			for (const cookie of this.cookieData) {
				if (cookie.cookieProvider.code === providerCode) {
					return cookie.category.code;
				}
			}
		}

		// Provider not found
		return null;
	}

	/**
	 * Event handling
	 */

	public on(
		event: ConsentEventType,
		callback: ConsentEventCallback,
	): () => void {
		if (!this.eventListeners.has(event)) {
			this.eventListeners.set(event, new Set());
		}

		this.eventListeners.get(event)!.add(callback);

		// If already initialized and registering 'init' event, call immediately
		if (event === "init" && this.initialized) {
			callback();
		}

		// Return unsubscribe function
		return () => {
			this.eventListeners.get(event)?.delete(callback);
		};
	}

	public off(event: ConsentEventType, callback: ConsentEventCallback): void {
		this.eventListeners.get(event)?.delete(callback);
	}

	private emit(event: ConsentEventType, data?: ConsentCategories): void {
		const listeners = this.eventListeners.get(event);
		if (listeners) {
			for (const callback of listeners) {
				try {
					callback(data);
				} catch (error) {
					console.error(
						`[DCBL Cookie Consent] Error in ${event} handler:`,
						error,
					);
				}
			}
		}
	}

	/**
	 * Internal helpers
	 */

	private buildConsentState(
		acceptAll: boolean,
		categoryOverrides?: Record<string, boolean>,
		serviceOverrides?: ServicesByCategory,
	): { categories: ConsentCategories; services: ServicesByCategory | undefined } {
		const categories: ConsentCategories = {};
		const providersByCategory = this.getProvidersByCategory();

		// When accepting all or rejecting all without per-service granularity,
		// don't create a services filter - this allows ALL providers to be unblocked
		// (including embed providers like YouTube that aren't in CMP data)
		const hasServiceOverrides = serviceOverrides && Object.keys(serviceOverrides).length > 0;
		const services: ServicesByCategory | undefined = hasServiceOverrides ? {} : undefined;

		for (const [key, storage] of Object.entries(this.config.storages)) {
			if (!storage?.enabled) continue;

			const allProviders = providersByCategory.get(key) ?? [];
			const allProviderCodes = allProviders.map((p) => p.code);

			if (serviceOverrides && key in serviceOverrides) {
				// Per-service override: category is true if any provider accepted
				services![key] = serviceOverrides[key]!;
				categories[key] = serviceOverrides[key]!.length > 0;
			} else if (categoryOverrides && key in categoryOverrides) {
				categories[key] = categoryOverrides[key]!;
				if (services) services[key] = categoryOverrides[key]! ? allProviderCodes : [];
			} else if (storage.readonly) {
				categories[key] = storage.default;
				if (services) services[key] = storage.default ? allProviderCodes : [];
			} else {
				categories[key] = acceptAll;
				if (services) services[key] = acceptAll ? allProviderCodes : [];
			}
		}

		return { categories, services };
	}

	private applyConsent(
		categories: ConsentCategories,
		services: ServicesByCategory | undefined,
		isFirstAction: boolean,
	): void {
		const previousConsent = this.currentConsent;
		this.currentConsent = categories;
		this.currentServices = services ?? null;

		// Determine changed categories
		const changedCategories: string[] = [];
		if (previousConsent) {
			for (const key of Object.keys(categories)) {
				if (previousConsent[key] !== categories[key]) {
					changedCategories.push(key);
				}
			}
		}

		// If CMP client is available, use processConsent for full flow
		// (saves cookie, updates Google Consent, sends to CMP, fires events, unblocks scripts)
		if (this.cmpClient) {
			processConsent(categories, {
				cmpClient: this.cmpClient,
				cookieConfig: {
					name: this.config.cookie.name,
					domain: this.config.cookie.domain || undefined,
					expiry: this.config.cookie.expiresAfterDays,
				},
				widgetConfig: {
					storages: this.config.storages,
					revision: this.config.revision,
					locale: this.config.locale,
				},
				revision: this.config.revision,
				cookieData: this.cookieData ?? undefined,
				autoClearCookies: true,
				services,
			});
		} else {
			// No CMP client - handle consent locally
			const cookieData: ConsentCookieData = {
				categories,
				...(services && { services }),
				revision: this.config.revision,
				timestamp: new Date().toISOString(),
				settingsChecksum: "",
			};

			setConsentCookie(cookieData, {
				name: this.config.cookie.name,
				domain: this.config.cookie.domain || undefined,
				expiry: this.config.cookie.expiresAfterDays,
			});

			updateGoogleConsent(categories);
			unblockScripts(categories, services);
			unblockIframes(categories, services);
			pushConsentEvents(categories, isFirstAction, changedCategories);
			dispatchConsentEvent({ categories, isFirstAction, changedCategories });
		}

		// Hide modal
		this.hideModal();

		// Emit internal events
		if (isFirstAction) {
			this.emit("consent:first-action", categories);
		} else if (changedCategories.length > 0) {
			this.emit("consent:changed", categories);
		}
		this.emit("consent:accepted", categories);
	}
}
